import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import { getPackagingMaterialList } from "services/data.internal.service";
import { deactivatePackagingMaterial } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { activatePackagingMaterial} from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { getPackagingMaterialStatusCount } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function PackagingMaterialList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["packaging-material-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Packaging Material"]) &&
      permissions["Packaging Material"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Packaging Material"]) &&
      permissions["Packaging Material"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Packaging Material"]) &&
      permissions["Packaging Material"].findIndex((b) => b === "deactivate") !== -1);

  useQuery({
    queryKey: ["packaging-material-list", { customerCode, active }],
    queryFn: () => getPackagingMaterialList({ active }),
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["packaging-material-list", "status", { customerCode }],
    queryFn: () => getPackagingMaterialStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivatePackagingMaterial, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activatePackagingMaterial, {
    onSuccess,
    onError,
  });

  const onNew = () => navigate("/setup/packaging-material/add");

  const onEdit = (req) => {
    navigate("/setup/packaging-material/edit", {
      state: req,
    });
  };

  const handleClose = () => setDialog(null);

  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.materialName}`}
        onClose={handleClose}
        onOk={() => {
          handleOk(req);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.materialName}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Material Name", accessorKey: "materialName" },
    { header: "Material Size", accessorKey: "materialSize" },
    { header: "Barcode", accessorKey: "barcode" },
    { header: "Type", accessorKey: "type" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { materialName } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit packaging material"
                disabled={!editPermission}
                onClick={() => {
                  onEdit({ materialName });
                }}
              />
              <DelButton
                title="deactivate packaging material"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({ materialName });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate packaging material"
              disabled={!editPermission}
              onClick={() => onActive({ materialName })}
            />
          );
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;packaging&nbsp;material
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default PackagingMaterialList;
